// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Every user-facing time renders through here, on the active site's IANA zone.
// `TimePreset` is the standard: a shape not listed is not a shape the UI shows.

import { parseInstant } from './time';

export type TimePreset =
  | 'clock'
  | 'clockSeconds'
  | 'hourAmPm'
  | 'weekdayShort'
  | 'dateShort'
  | 'dateMedium'
  | 'dateFull'
  | 'dateTime'
  | 'dateTimeShort';

// hourCycle 'h23', not hour12:false — the latter renders midnight as "24:00" on some ICU builds.
const PRESET_OPTIONS: Record<TimePreset, Intl.DateTimeFormatOptions> = {
  clock: { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' },
  clockSeconds: { hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' },
  hourAmPm: { hour: 'numeric', hour12: true },
  weekdayShort: { weekday: 'short' },
  dateShort: { month: 'short', day: 'numeric' },
  dateMedium: { month: 'short', day: 'numeric', year: 'numeric' },
  dateFull: { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' },
  dateTime: {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  },
  dateTimeShort: {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  },
};

/** Locale is pinned so output never drifts with the viewer's machine settings. */
const LOCALE = 'en-US';

/** Fallback string for an absent or unparseable value. */
export const NO_TIME = '—';

export interface ResolvedZone {
  /** Always a valid IANA id. */
  timeZone: string;
  /** Short zone name for display, e.g. "EDT" or "GMT+5:45". */
  label: string;
  /** True when the site's zone was absent or unusable and this is the viewer's. */
  isFallback: boolean;
}

// Intl.DateTimeFormat construction is expensive and alarm rows format per render.
const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatter(preset: TimePreset, timeZone: string): Intl.DateTimeFormat {
  const key = `${preset}|${timeZone}`;
  let cached = formatterCache.get(key);
  if (!cached) {
    cached = new Intl.DateTimeFormat(LOCALE, { ...PRESET_OPTIONS[preset], timeZone });
    formatterCache.set(key, cached);
  }
  return cached;
}

export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

const zoneValidity = new Map<string, boolean>();

/** Whether `tz` is a zone `Intl` accepts — an unknown id throws RangeError. */
export function isValidTimeZone(tz: string | null | undefined): boolean {
  if (!tz) return false;
  const cached = zoneValidity.get(tz);
  if (cached !== undefined) return cached;
  let valid = true;
  try {
    new Intl.DateTimeFormat(LOCALE, { timeZone: tz });
  } catch {
    valid = false;
  }
  zoneValidity.set(tz, valid);
  return valid;
}

/** The short zone name at `at`, since it changes across a DST boundary. */
export function zoneAbbreviation(timeZone: string, at: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat(LOCALE, { timeZone, timeZoneName: 'short' }).formatToParts(
    at
  );
  return parts.find(p => p.type === 'timeZoneName')?.value ?? timeZone;
}

/**
 * The zone id alone, for callers that never show a label — resolving a whole
 * `ResolvedZone` builds an `Intl.DateTimeFormat` for the abbreviation.
 */
export function resolveZoneId(candidate: string | null | undefined): string {
  return isValidTimeZone(candidate) ? (candidate as string) : browserTimeZone();
}

/** A site's zone, or the viewer's when the site has none or names one Intl rejects. */
export function resolveZone(
  candidate: string | null | undefined,
  at: Date = new Date()
): ResolvedZone {
  const timeZone = resolveZoneId(candidate);
  return {
    timeZone,
    label: zoneAbbreviation(timeZone, at),
    isFallback: !isValidTimeZone(candidate),
  };
}

function toDate(value: string | Date | null | undefined): Date | undefined {
  if (value == null || value === '') return undefined;
  const date = typeof value === 'string' ? parseInstant(value) : value;
  return Number.isNaN(date.getTime()) ? undefined : date;
}

/** Render an instant in `timeZone` using one of the canonical presets. */
export function formatInZone(
  value: string | Date | null | undefined,
  preset: TimePreset,
  timeZone: string,
  fallback: string = NO_TIME
): string {
  const date = toDate(value);
  if (!date) return fallback;
  return polish(formatter(preset, timeZone).format(date), preset);
}

// Intl separates date from time with ", " and writes "2 PM"; the house style is a space and "2pm".
const DATE_TIME_SEP = /,\s(?=\d{2}:\d{2})/;

function polish(text: string, preset: TimePreset): string {
  if (preset === 'dateTime') return text.replace(DATE_TIME_SEP, ' ');
  if (preset === 'dateTimeShort') return text.replace(DATE_TIME_SEP, ' \u00b7 ');
  // Intl may separate hour from AM/PM with U+202F or U+00A0, not a plain space.
  if (preset === 'hourAmPm') return text.replace(/\s+/gu, '').toLowerCase();
  return text;
}

const PART_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
};

const partsCache = new Map<string, Intl.DateTimeFormat>();

function wallClockParts(at: Date, timeZone: string) {
  let fmt = partsCache.get(timeZone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(LOCALE, { ...PART_OPTIONS, timeZone });
    partsCache.set(timeZone, fmt);
  }
  const parts = fmt.formatToParts(at);
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find(p => p.type === type)?.value ?? 0);
  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour: read('hour'),
    minute: read('minute'),
    second: read('second'),
  };
}

/** Signed minutes from `at` to `timeZone`'s wall clock; asked of ICU per instant, so DST-correct. */
export function zoneOffsetMinutes(at: Date, timeZone: string): number {
  const p = wallClockParts(at, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return Math.round((asUtc - at.getTime()) / 60_000);
}

/** Minutes since midnight on `timeZone`'s clock, for intraday window checks. */
export function zonedMinuteOfDay(at: Date, timeZone: string): number {
  const p = wallClockParts(at, timeZone);
  return p.hour * 60 + p.minute;
}

/** `YYYY-MM-DD` for the calendar day `at` falls on in `timeZone`. */
export function zonedDayKey(at: Date, timeZone: string): string {
  const p = wallClockParts(at, timeZone);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/**
 * The `YYYY-MM-DD` key one calendar day after `key`.
 *
 * Adding 24 hours to an instant is not "the next day": across a DST fall-back
 * the site's clock runs a 25-hour day and lands back on the same key. Keys are
 * incremented as keys, which no offset change can perturb.
 */
export function nextDayKey(key: string): string {
  const [year, month, day] = key.slice(0, 10).split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day + 1)).toISOString().slice(0, 10);
}

/** A Date whose browser-local wall clock equals `at` in `timeZone` — a counterfeit instant, display only. */
export function toZonedWallClock(at: Date, timeZone: string): Date {
  const browserOffset = -at.getTimezoneOffset();
  return new Date(at.getTime() + (zoneOffsetMinutes(at, timeZone) - browserOffset) * 60_000);
}

/** Render a `YYYY-MM-DD` day. Pinned to UTC: converting a calendar day slides it for half the planet. */
export function formatCalendarDate(
  dateOnly: string | null | undefined,
  preset: 'dateShort' | 'dateMedium' = 'dateMedium',
  fallback: string = NO_TIME
): string {
  if (!dateOnly) return fallback;
  return formatInZone(`${dateOnly.slice(0, 10)}T00:00:00Z`, preset, 'UTC', fallback);
}
